import { Auth0AccountRole } from "client/openapi";

/**
 * The searches the CRM offers, in Search and in a board column's rule
 * alike: one kind of person, or one kind filtered by the people linked to
 * them (a combined search).
 */
export type SearchType =
  | "get_students"
  | "get_parents"
  | "get_tutors"
  | "get_students_with_parents"
  | "get_parents_with_students"
  | "get_students_with_tutors"
  | "get_tutors_with_students";

export type PersonKind = "students" | "parents" | "tutors";

/** Who each search returns (A) and, for the combined searches, who filters it (B). */
export const SEARCH_PEOPLE: Record<
  SearchType,
  [PersonKind, PersonKind | null]
> = {
  get_students: ["students", null],
  get_parents: ["parents", null],
  get_tutors: ["tutors", null],
  get_students_with_parents: ["students", "parents"],
  get_parents_with_students: ["parents", "students"],
  get_students_with_tutors: ["students", "tutors"],
  get_tutors_with_students: ["tutors", "students"],
};

export const SEARCH_TYPE_OPTIONS: { value: SearchType; label: string }[] = [
  { value: "get_students", label: "Get Students" },
  { value: "get_parents", label: "Get Parents" },
  { value: "get_tutors", label: "Get Tutors" },
  {
    value: "get_students_with_parents",
    label: "Get Students (with filtering by parent criteria)",
  },
  {
    value: "get_parents_with_students",
    label: "Get Parents (with filtering by student criteria)",
  },
  {
    value: "get_students_with_tutors",
    label: "Get Students (with filtering by tutor criteria)",
  },
  {
    value: "get_tutors_with_students",
    label: "Get Tutors (with filtering by student criteria)",
  },
];

export const PERSON_TITLES: Record<PersonKind, string> = {
  students: "Students",
  parents: "Parents",
  tutors: "Tutors",
};

/** Each kind of person's role: whose built-in fields a group offers. */
export const ROLE_OF: Record<PersonKind, Auth0AccountRole> = {
  students: Auth0AccountRole.ME,
  parents: Auth0AccountRole.PARENT,
  tutors: Auth0AccountRole.ORG_TUTOR,
};

/** The kind of person a role is. */
export function kindOf(role: Auth0AccountRole | null | undefined): PersonKind {
  return role === Auth0AccountRole.PARENT
    ? "parents"
    : role === Auth0AccountRole.ORG_TUTOR
    ? "tutors"
    : "students";
}

/** The search that returns one kind of person, filtered by another or not. */
export function searchTypeOf(
  kind: PersonKind,
  filteredBy: PersonKind | null
): SearchType {
  const found = (Object.keys(SEARCH_PEOPLE) as SearchType[]).find(
    (type) =>
      SEARCH_PEOPLE[type][0] === kind && SEARCH_PEOPLE[type][1] === filteredBy
  );
  return found ?? searchTypeOf(kind, null);
}
